// Central data for all service pages and navigation.

export const SERVICES = {
  "social-media-management": {
    slug: "social-media-management",
    title: "Social Media Management",
    label: "Social Media Management",
    hero: "Social media with purpose.",
    shortDesc: "Strategic social media management, planned, created and delivered with purpose.",
    intro:
      "Done-for-you content, strategy and support so you can stay consistent, show up with confidence and get results. We don't post for the sake of it. Every post has a job to do, whether that's building visibility, earning trust, educating your audience, encouraging engagement or driving enquiries.",
    problem: [
      "You are posting, but not sure it is working.",
      "Consistency slips whenever the week gets busy.",
      "There is no clear plan behind what you share.",
      "Reporting is vague, so decisions are guesswork.",
    ],
    approach:
      "We build a monthly content plan around clear pillars, then handle the writing, design, scheduling and reporting. You review, we refine, and your marketing keeps moving without relying on last-minute content or stretched internal resources.",
    included: [
      "Monthly content plan",
      "Captions and hashtags",
      "Canva design",
      "Scheduling and platform optimisation",
      "Reporting and feedback rounds",
      "Engagement support where included",
      "Monthly calls where included",
    ],
    whoFor: [
      "Founder-led businesses that don't have time to post consistently",
      "Teams that need direction, not just an extra pair of hands",
      "Brands ready to show up properly across one or more platforms",
    ],
    outcomes: [
      "A consistent, professional presence",
      "Content with a clear purpose",
      "Clear reporting and priorities",
      "Time back to run your business",
    ],
    packages: [
      {
        name: "BASE",
        price: "£525",
        period: "/month",
        subtitle: "Start strong. Stay consistent.",
        note: "For brands that want clarity and support without overwhelm.",
        features: [
          "3 posts per week",
          "One primary social platform",
          "Captions and hashtags",
          "Monthly content plan",
          "Canva design",
          "Scheduling via Vista Social or equivalent",
          "1 round of feedback",
          "Light monthly insights",
          "30-minute monthly strategy and planning call",
        ],
      },
      {
        name: "FLOW",
        price: "£725",
        period: "/month",
        subtitle: "Build momentum. Grow with intention.",
        note: "For brands ready to build a consistent presence across platforms.",
        highlighted: true,
        features: [
          "4 posts per week",
          "Up to 2 platforms",
          "Multi-platform scheduling",
          "Platform-specific captions and designs",
          "First-comment hashtags where relevant",
          "Monthly 60-minute strategy and planning session",
          "Priority feedback turnaround",
          "Basic analytics report",
          "Light engagement support",
        ],
      },
      {
        name: "PARTNER",
        price: "£995",
        period: "/month",
        subtitle: "High support. Full content partnership.",
        note: "For clients who want to hand over social media and grow with purpose.",
        features: [
          "5 posts per week",
          "Up to 3 platforms",
          "Full scheduling and optimisation",
          "Weekly content coordination",
          "Canva and custom graphics",
          "Monthly 60-minute strategy call",
          "Enhanced analytics and reporting",
          "Engagement and inbox flagging",
          "Ongoing comms via Slack or WhatsApp",
        ],
      },
    ],
    addons: [
      "Extra posts from £75/month",
      "Additional platform management £125/month",
      "Story design templates £75 (one-off)",
      "Reels formatting £95/month",
      "Extra engagement support £95/month",
      "Competitor & hashtag refresh £75 to £95",
    ],
    howWeWork: {
      buildCopy: "We create, design and implement the content your strategy needs.",
      supportCopy: "We review, refine and evolve your marketing as your business grows.",
    },
    cta: {
      heading: "Let's build a social media presence that works as hard as your business does.",
      copy: "Book a discovery call and we'll talk through your business, your goals and the most effective way to get there.",
    },
    combinedWith: [
      { label: "Content Strategy", slug: "content-strategy" },
      { label: "Blog Writing & SEO", slug: "content-strategy", hash: "blog-writing" },
      { label: "Website Design", slug: "website-design" },
    ],
    faq: [
      { q: "Which platforms do you cover?", a: "Instagram, LinkedIn, Facebook and others. We recommend the platforms that fit your audience and goals rather than spreading thin across all of them." },
      { q: "Do I need to supply content?", a: "No. We plan, write and design. You review and approve. Where you have photos or footage, we will use them." },
      { q: "Can I change package later?", a: "Yes. We shape the level of support around your goals and capacity, and adjust as things grow." },
    ],
  },

  "content-strategy": {
    slug: "content-strategy",
    title: "Content Strategy",
    label: "Content Strategy",
    hero: "Content without strategy is just noise.",
    shortDesc:
      "Create a content strategy that gives every piece of content a clear purpose and supports your wider business goals.",
    intro:
      "Create a content strategy that supports your business goals and gives every piece of content a purpose. Whether you're posting without direction, stuck in planning mode or launching something new, we'll help you build a plan you can actually follow.",
    problem: [
      "You are posting without a clear direction.",
      "You are stuck in planning mode and never quite start.",
      "You are launching something and need a plan that holds together.",
      "Your message changes depending on the day.",
    ],
    approach:
      "We audit what you have, define your messaging and content pillars, then build a practical content plan you can actually follow. The result is clarity, confidence and direction, not just ideas.",
    included: [
      "Platform audit",
      "Messaging guidance",
      "Content pillars and themes",
      "Organic funnel",
      "Content formats",
      "Posting rhythm",
      "Creative direction",
      "Campaign ideas",
      "4-week content plan",
      "Blog writing and SEO articles",
    ],
    whoFor: [
      "Businesses posting without direction",
      "Founders stuck in planning mode",
      "Anyone launching a new offer or brand",
    ],
    outcomes: [
      "A clear message and pillars",
      "A practical content plan",
      "Confidence in what to post and why",
      "A repeatable content rhythm",
    ],
    packages: [
      {
        name: "Strategy Snapshot",
        price: "£375",
        period: "one-off",
        subtitle: "A focused reset for one platform or campaign.",
        features: [
          "90-minute strategy call",
          "Audit of one platform or campaign",
          "Messaging and positioning guidance",
          "Real-time recommendations",
          "Summary email with action points",
          "Session recording",
        ],
      },
      {
        name: "Content Roadmap",
        price: "£695",
        period: "one-off",
        subtitle: "A full content plan you can follow.",
        highlighted: true,
        features: [
          "Content and platform audit up to 2 channels",
          "60-minute strategy call",
          "Content pillars and messaging themes",
          "Organic funnel outline",
          "Format guidance",
          "4-week example content plan",
          "Creative direction",
          "7 days of follow-up support",
        ],
      },
      {
        name: "Strategy Partner",
        price: "£795",
        period: "/month",
        subtitle: "Ongoing strategic direction. Also from £2,100 per project.",
        features: [
          "Monthly 60-minute strategy calls",
          "Funnel, launch or campaign planning",
          "Messaging and offer refinement",
          "Platform and content reviews",
          "Quarterly analytics check-in",
          "WhatsApp access for reviews",
          "Clear action plans",
        ],
      },
    ],
    addons: [],
    combinedWith: [
      { label: "Social Media Management", slug: "social-media-management" },
      { label: "Marketing Strategy", slug: "marketing-strategy" },
      { label: "Website Design", slug: "website-design" },
    ],
    howWeWork: {
      buildCopy: "We create, design and implement the content your strategy needs.",
      supportCopy: "We review, refine and evolve your marketing as your business grows.",
    },
    cta: {
      heading: "Let's build content that supports your marketing, not just fills your calendar.",
      copy: "Book a discovery call and we'll talk through your business, your goals and the most effective way to move forward.",
    },
    faq: [
      { q: "Is this different from social media management?", a: "Yes. Strategy defines the direction. Management delivers it. Many clients start with strategy, then move into ongoing management." },
      { q: "Will I be able to run the plan myself?", a: "Absolutely. The plan is built to be practical and usable, whether you deliver it in-house or hand it back to us." },
    ],
    blogWriting: {
      label: "Blog Writing & SEO",
      heading: "Blogs that give your content somewhere to lead.",
      intro:
        "Social media brings people to the door. Well-written, search-friendly blogs give them a reason to stay, understand you properly and take the next step. We research, write and optimise articles that support your wider strategy, then hand you clear ways to repurpose them across social and email.",
      options: [
        {
          name: "Standard Blog",
          meta: "1,000 to 1,500 words",
          price: "£195",
          subtitle: "A focused, well-written article on a topic that matters to your audience.",
          idealFor: ["SMEs", "Existing social media clients", "One-off articles"],
        },
        {
          name: "Premium Blog",
          meta: "1,500 to 2,000 words",
          price: "£295",
          subtitle: "A fully researched, SEO-optimised article built to rank and repurpose.",
          includes: [
            "Research",
            "SEO optimisation",
            "Suggested internal and external links",
            "Meta title",
            "Meta description",
            "Call to action",
            "Repurposing suggestions",
          ],
        },
      ],
      monthlyPackages: {
        label: "Monthly Content Packages",
        heading: "Ongoing blogs, on a rhythm.",
        note: "Save vs single-blog pricing when you commit to a monthly cadence.",
        options: [
          { name: "2 blogs per month", price: "From £350", period: "/month" },
          { name: "4 blogs per month", price: "From £650", period: "/month" },
        ],
      },
    },
  },

  "website-design": {
    slug: "website-design",
    title: "Website Design",
    label: "Website Design",
    flagship: true,
    hero: "Websites built with strategy, style and purpose.",
    shortDesc:
      "Strategy-led websites designed to support your business, strengthen your brand and convert visitors into enquiries.",
    positioning: "Websites are no longer digital brochures. They are business tools.",
    intro:
      "Strategy-led websites designed to support your business, strengthen your brand and convert visitors into enquiries. Every website should help people understand what you do, trust your business and take the next step.",
    problem: [
      "Your website does not reflect the quality of your business.",
      "Visitors do not understand what you do quickly enough.",
      "The site looks fine but rarely generates enquiries.",
      "The mobile experience lets you down.",
    ],
    approach:
      "We start with strategy and structure, shape the messaging and user journey, then design and build on the platform that's right for your business. Whether that's Emergent, Wix Studio or WordPress, every website is built around clarity, performance and future growth. Where needed, we also support landing pages and campaign microsites. Brand identity projects are delivered through our trusted creative partners.",
    included: [
      "Website strategy and structure",
      "UX planning",
      "Copy and visual direction",
      "Multi-platform website design",
      "Landing and campaign pages",
      "Lead-generation websites",
      "Blog setup and SEO foundations",
      "Mobile optimisation",
      "Contact forms and booking links",
      "CRM integrations and analytics setup",
      "AI-enhanced workflows",
      "Client portal planning",
    ],
    whoFor: [
      "Founder-led businesses that have outgrown their current site",
      "Businesses launching a new service or campaign",
      "Businesses whose website no longer reflects the quality of their work",
    ],
    outcomes: [
      "Clearer messaging",
      "A better user journey",
      "Stronger enquiries",
      "A more professional presence",
      "Improved trust and mobile experience",
      "More joined-up marketing",
    ],
    entryComparison: [
      {
        headline: "Not sure where to start?",
        subhead:
          "A quick at-a-glance view of our three entry points. Full details in Ways to Work below.",
      },
      {
        name: "Website Snapshot",
        price: "£295",
        bestFor: "You already have a site, but suspect it is underselling you.",
        timeline: "1 week turnaround",
        deliverable: "A prioritised review with clear recommendations you can action.",
      },
      {
        name: "Website Roadmap",
        price: "£595",
        bestFor: "You want to plan properly before a rebuild or new build.",
        timeline: "2 to 3 weeks",
        deliverable: "Sitemap, messaging plan and visual direction, ready to hand to any designer.",
      },
      {
        name: "Website Build",
        price: "From £1,495",
        bestFor: "You are ready to launch a considered, strategy-led website.",
        timeline: "4 to 8 weeks",
        deliverable: "A live, mobile-optimised site up to 10 pages, built on the right platform for you.",
      },
    ],
    packages: [
      {
        name: "Website Snapshot",
        price: "£295",
        period: "",
        subtitle: "Audit and strategic recommendations for an existing website.",
        features: [
          "Full website review",
          "Messaging and structure assessment",
          "Prioritised recommendations",
        ],
      },
      {
        name: "Website Roadmap",
        price: "£595",
        period: "",
        subtitle: "Structure, messaging and design direction before a build.",
        features: [
          "Site structure and sitemap",
          "Messaging and content plan",
          "Visual direction",
        ],
      },
      {
        name: "Website Build",
        price: "From £1,495",
        period: "",
        subtitle: "Professional websites built around your business, goals and future growth.",
        highlighted: true,
        features: [
          "Strategy-led design",
          "Built in Emergent, Wix Studio or WordPress",
          "Up to 10 pages*",
          "Mobile optimisation",
          "SEO foundations",
          "Analytics setup",
          "Copy refinement using client-supplied content",
        ],
        footnote: "*Larger or more complex websites are quoted individually.",
      },
      {
        name: "Landing Page Build",
        price: "From £595",
        period: "",
        subtitle: "Focused page for a campaign, launch or service.",
        standalone: true,
        features: [
          "Conversion-focused layout",
          "Copy direction",
          "Form and tracking setup",
        ],
      },
      {
        name: "Website Messaging & Copywriting",
        price: "From £395",
        period: "",
        subtitle: "Refine the messaging on your website so it's clearer, more confident and more likely to convert.",
        standalone: true,
        features: [
          "Website messaging",
          "Page copy",
          "Calls to action",
          "SEO optimisation",
          "Tone of voice development",
        ],
      },
      {
        name: "Website Partner",
        price: "POA",
        period: "",
        subtitle: "Ongoing improvements, content updates and performance reviews.",
        standalone: true,
        features: [
          "Content updates",
          "New landing pages",
          "Performance reviews",
        ],
      },
    ],
    addons: [],
    combinedWith: [
      { label: "Website Roadmap", slug: "website-design", hash: "packages" },
      { label: "Blog Writing & SEO", slug: "content-strategy", hash: "blog-writing" },
      { label: "Social Media Management", slug: "social-media-management" },
    ],
    faq: [
      { q: "Which platform will you build my website on?", a: "It depends entirely on your needs, your budget and how much you want to manage the site yourself. We build in Emergent, Wix Studio and WordPress, and can advise on the right fit rather than pushing a single platform. The goal is a website you can actually live with, not one that only we can touch." },
      { q: "Can you maintain the website once it is live?", a: "Yes. A website is never really finished, so we offer ongoing care, updates and improvements where you want them. Where you would prefer to manage it in-house, we build on a platform your team can confidently update and leave you with clear guidance so you are never locked in." },
    ],
    howWeWork: {
      buildCopy: "We create, design and implement what your strategy needs.",
      supportCopy: "We review, refine and evolve your website as your business grows.",
    },
    cta: {
      heading: "Let's build a website that works as hard as your business does.",
      copy: "Book a discovery call and we'll talk through your business, your goals and the best way to bring your website to life.",
    },
  },

  "ai-consultancy": {
    slug: "ai-consultancy",
    title: "AI Consultancy",
    label: "AI Consultancy",
    flagship: true,
    hero: "AI that makes business simpler.",
    shortDesc:
      "Practical AI consultancy that helps businesses save time, strengthen systems and work more efficiently.",
    positioning: "Helping businesses use AI with confidence, clarity and common sense.",
    intro:
      "Practical AI consultancy that helps businesses save time, strengthen systems and work more efficiently. Track Digital helps businesses understand where AI fits, where it doesn't and how to build practical workflows that support real people doing real work. AI should support people, not replace them.",
    problem: [
      "AI feels useful, but overwhelming.",
      "There are too many tools and no clear starting point.",
      "Your team is curious but unsure what is safe or sensible.",
      "You want time back, not more complexity.",
    ],
    approach:
      "We identify practical use cases, build reusable workflows and prompt libraries, then train your team so AI becomes a calm, everyday tool rather than another source of complexity.",
    included: [
      "AI Readiness Review",
      "AI Strategy Sessions",
      "AI Training for Teams",
      "Prompt Engineering and Prompt Libraries",
      "Custom GPTs",
      "AI Content Workflows",
      "AI Marketing Systems",
      "Internal Knowledge Bases",
      "AI-assisted Research and Reporting Workflows",
      "Workflow Automation",
      "AI Policy and Usage Guidance",
    ],
    whoFor: [
      "Small teams spending too much time on repetitive admin",
      "Founders who want practical AI, not hype",
      "Businesses looking to build smarter content and marketing systems",
    ],
    outcomes: [
      "Clear AI use cases",
      "Reduced admin",
      "Better content workflows",
      "Reusable prompt systems",
      "More confident teams",
      "A practical implementation plan",
    ],
    packages: [
      {
        name: "AI Snapshot",
        price: "£395",
        period: "",
        subtitle: "A focused session to identify quick wins and practical opportunities for AI in your business.",
        features: [
          "90-minute discovery workshop",
          "AI maturity review",
          "Business process discussion",
          "Quick-win recommendations",
          "Prioritised action plan",
          "Summary document",
        ],
      },
      {
        name: "AI Roadmap",
        price: "£895",
        period: "",
        subtitle: "A full review with recommendations, tools and an implementation plan.",
        highlighted: true,
        features: [
          "Business process review",
          "Workflow mapping",
          "AI tool recommendations",
          "Prompt framework",
          "Automation opportunities",
          "Priority implementation roadmap",
        ],
      },
      {
        name: "AI Partner",
        price: "From £795",
        period: "/month",
        subtitle: "Ongoing AI advisory, workflow development and team training.",
        features: [
          "Monthly AI strategy sessions",
          "Workflow optimisation",
          "Prompt library development",
          "Team training",
          "AI implementation support",
          "Quarterly AI reviews",
        ],
      },
    ],
    addons: [],
    combinedWith: [
      { label: "Website Design", slug: "website-design" },
      { label: "Marketing Strategy", slug: "marketing-strategy" },
      { label: "Social Media Management", slug: "social-media-management" },
    ],
    combinedWithIntro: "Most clients combine AI consultancy with one or two of the services below to create more efficient, joined-up ways of working.",
    howWeWork: {
      buildCopy: "We create, implement and refine the AI systems your business needs.",
      supportCopy: "We review, refine and evolve your AI workflows as your business grows.",
    },
    cta: {
      heading: "Let's make AI work for your business.",
      copy: "Book a discovery call and we'll explore where AI can genuinely save time, improve consistency and support your team.",
    },
    faq: [
      { q: "Will AI replace my team?", a: "No. Our approach is to support people, not replace them. We automate the busywork so your team can focus on judgement and relationships." },
      { q: "We are not technical. Is that a problem?", a: "Not at all. We translate AI into practical, everyday workflows and train your team so it feels natural to use." },
    ],
  },

  "marketing-strategy": {
    slug: "marketing-strategy",
    title: "Marketing Strategy",
    label: "Marketing Strategy",
    hero: "Clear strategy before more marketing.",
    shortDesc:
      "Build a clear marketing strategy before investing time, money or effort into delivery.",
    positioning: "Strategy before activity.",
    intro:
      "Build a clear marketing plan before investing time, money or effort into marketing activity. Track helps define the message, audience, offer, priorities and content structure.",
    problem: [
      "You are busy, but not necessarily moving forward.",
      "Activity is high, but direction is unclear.",
      "You are unsure which channels actually matter.",
      "Decisions are made on instinct rather than priorities.",
    ],
    approach:
      "We audit, define your message and audience, then set clear priorities and a plan. Strategy first, so every future activity has a reason behind it.",
    included: [
      "Marketing audit",
      "Messaging and positioning support",
      "Campaign planning",
      "Organic funnel planning",
      "Launch strategy",
      "Content pillars",
      "Audience mapping",
      "Competitor review",
      "Channel recommendations",
      "Quarterly marketing plan",
    ],
    whoFor: [
      "Businesses looking for clarity before investing in marketing",
      "Teams doing a lot without a plan",
      "Businesses preparing for a launch or new phase",
    ],
    outcomes: [
      "A clear message and audience",
      "Defined priorities",
      "A practical marketing plan",
      "Confident decision making",
    ],
    packages: [
      {
        name: "Strategy Snapshot",
        price: "£375",
        period: "one-off",
        subtitle: "A focused strategy reset.",
        features: [
          "90-minute strategy call",
          "Audit of one platform or campaign",
          "Messaging and positioning guidance",
          "Real-time recommendations",
          "Summary email with action points",
          "Session recording",
        ],
      },
      {
        name: "Content Roadmap",
        price: "£695",
        period: "one-off",
        subtitle: "A full plan you can follow.",
        highlighted: true,
        features: [
          "Content and platform audit up to 2 channels",
          "60-minute strategy call",
          "Content pillars and messaging themes",
          "Organic funnel outline",
          "Format guidance",
          "4-week example content plan",
          "Creative direction",
          "7 days of follow-up support",
        ],
      },
      {
        name: "Strategy Partner",
        price: "£795",
        period: "/month",
        subtitle: "Ongoing partnership. Also from £2,100 per project.",
        features: [
          "Monthly 60-minute strategy calls",
          "Funnel, launch or campaign planning",
          "Messaging and offer refinement",
          "Platform and content reviews",
          "Quarterly analytics check-in",
          "Slack or WhatsApp access for reviews",
          "Clear action plans",
        ],
      },
    ],
    addons: [],
    combinedWith: [
      { label: "Content Strategy", slug: "content-strategy" },
      { label: "Social Media Management", slug: "social-media-management" },
      { label: "Website Design", slug: "website-design" },
    ],
    faq: [
      { q: "How is this different from content strategy?", a: "Marketing strategy sets the overall direction across channels. Content strategy focuses on what and how you publish. They work well together." },
      { q: "Do you deliver the strategy or just advise?", a: "Both. We can hand you a plan to run in-house, or move into ongoing delivery as a partner." },
    ],
  },

  "social-media-design": {
    slug: "social-media-design",
    title: "Social Media Design",
    label: "Social Media Design",
    hero: "Design that supports your strategy, not a full rebrand.",
    shortDesc:
      "Social graphics and creative assets designed to support your marketing and strengthen your brand.",
    positioning: "Clean, consistent graphics that help content stand out.",
    intro:
      "Social graphics and creative assets designed to support your marketing and strengthen your brand. We work with your existing brand guidelines or visual style and elevate what you already have, so every post looks considered and consistent. For full brand identity projects, we work alongside trusted branding partners.",
    problem: [
      "Your content looks inconsistent.",
      "Designing everything yourself is a drain.",
      "Your visuals do not match the quality of your work.",
      "You have a brand, but no system to apply it.",
    ],
    approach:
      "We take your existing brand and build clean, consistent templates and graphics that make every post feel considered. For full branding projects, we work with trusted partners.",
    included: [
      "Static post design",
      "Carousel design",
      "Story templates",
      "Quote cards",
      "Branded graphics",
      "Post covers",
      "Canva templates",
      "Social media design bundles",
      "Monthly design support",
    ],
    whoFor: [
      "Brands with guidelines but no design capacity",
      "Founders who want a polished, consistent feed",
      "Teams that need reusable templates",
    ],
    outcomes: [
      "A consistent visual style",
      "Content that stands out",
      "Reusable templates",
      "Less design stress",
    ],
    packages: [
      { name: "5-Pack Carousels", price: "£200", period: "", subtitle: "Five carousel graphics designed to your brand.", features: ["5 carousel designs", "On-brand styling", "Editable format"] },
      { name: "Template Bundle", price: "£275", period: "", subtitle: "Carousels, quotes and covers in one bundle.", highlighted: true, features: ["Carousel templates", "Quote card templates", "Post cover templates", "Built in Canva"] },
      { name: "Content Rebrand", price: "POA", period: "", subtitle: "Rebrand of existing content into a new style.", features: ["Visual style refresh", "Template rebuild", "Applied across formats"] },
    ],
    addons: [
      "5-pack of carousel graphics - £200",
      "Template bundle including carousel, quotes and covers - £275",
      "Rebrand of existing content into new style - POA",
    ],
    combinedWith: [
      { label: "Social Media Management", slug: "social-media-management" },
      { label: "Content Strategy", slug: "content-strategy" },
      { label: "Website Design", slug: "website-design" },
    ],
    faq: [
      { q: "Do you create brand identities?", a: "This service works with your existing brand. For full brand identity, we collaborate with trusted partners." },
      { q: "Which tools do you design in?", a: "Primarily Canva, so your team can edit and reuse templates easily. We can work in other tools where needed." },
    ],
  },

  "marketing-automation": {
    slug: "marketing-automation",
    title: "Marketing Automation",
    label: "Marketing Automation",
    hero: "Better systems. Less busywork.",
    shortDesc:
      "Practical automation that reduces repetitive tasks, improves consistency and keeps your marketing running smoothly.",
    positioning: "Practical systems that reduce admin and keep marketing moving.",
    intro:
      "Practical automation that reduces repetitive tasks and keeps your marketing running smoothly. We build systems that reduce manual admin and keep marketing moving. This connects closely with our AI Consultancy work.",
    problem: [
      "Manual admin is eating your time.",
      "Follow-up is slow or inconsistent.",
      "Reporting is pieced together by hand.",
      "Your processes live in people's heads.",
    ],
    approach:
      "We map your processes, then build lead capture, email, reporting and approval workflows that run reliably in the background, with AI assistance where it helps.",
    included: [
      "Lead capture workflows",
      "Newsletter and email marketing setup",
      "CRM support",
      "Automated reporting",
      "Client onboarding forms",
      "Proposal and content approval workflows",
      "Google Drive, Trello or Notion setup",
      "Zapier or Make style automation concepts",
      "AI-assisted admin workflows",
      "Repurposing systems",
    ],
    whoFor: [
      "Small teams with repetitive admin",
      "Founders who want faster follow-up",
      "Businesses scaling their marketing",
    ],
    outcomes: [
      "Less manual admin",
      "Clearer processes",
      "Faster follow-up",
      "A better client experience",
      "More consistent marketing",
    ],
    packages: [
      { name: "Automation Snapshot", price: "POA", period: "", subtitle: "Identify the workflows worth automating first.", features: ["Process review", "Quick-win automations", "Recommendations"] },
      { name: "Automation Build", price: "POA", period: "", subtitle: "Design and build of your priority workflows.", highlighted: true, features: ["Workflow design", "Tool setup", "Testing and handover"] },
      { name: "Automation Partner", price: "POA", period: "", subtitle: "Ongoing systems support and improvement.", features: ["New workflows", "Maintenance", "Reporting automation"] },
    ],
    addons: [],
    combinedWith: [
      { label: "AI Consultancy", slug: "ai-consultancy" },
      { label: "Virtual Marketing Support", slug: "virtual-marketing-support" },
      { label: "Website Design", slug: "website-design" },
    ],
    faq: [
      { q: "Which tools do you use?", a: "We work with the tools that fit your business, including Zapier, Make, Notion, Trello and common CRMs, with AI assistance where it adds value." },
      { q: "Is this the same as AI Consultancy?", a: "They overlap. Automation focuses on systems and workflows. AI Consultancy focuses on where AI fits. We often deliver them together." },
    ],
  },

  "virtual-marketing-support": {
    slug: "virtual-marketing-support",
    title: "Virtual Marketing Support",
    label: "Virtual Marketing Support",
    hero: "Support that actually supports.",
    shortDesc:
      "Flexible behind-the-scenes marketing support that keeps projects organised, marketing moving and everything running smoothly.",
    positioning: "Smart, reliable, detail-driven support for businesses that need help behind the scenes.",
    intro:
      "Flexible behind-the-scenes marketing support that keeps projects organised, marketing moving and day-to-day tasks under control. Reliable, detail-driven help for the marketing tasks that keep everything running. Practical, human and genuinely useful.",
    problem: [
      "Small tasks pile up and slow you down.",
      "You need help, but not another full-time hire.",
      "Behind-the-scenes work never quite gets done.",
      "You want a reliable, flexible pair of hands.",
    ],
    approach:
      "We agree a task list and rhythm, then handle the uploads, formatting, scheduling and admin that keep your marketing moving, with clear weekly check-ins.",
    included: [
      "Uploading blogs and formatting newsletters",
      "Scheduling emails and posts",
      "Updating websites",
      "Creating Trello, Notion or Canva templates",
      "DM flagging and LinkedIn follow-ups",
      "Podcast and YouTube uploads",
      "Lead magnet setup",
      "Research and repurposing content",
      "Deck formatting",
    ],
    whoFor: [
      "Growing businesses that need reliable marketing support without hiring in-house",
      "Small teams at capacity",
      "Businesses with recurring marketing admin",
    ],
    outcomes: [
      "Tasks handled reliably",
      "More time to focus",
      "Consistent behind-the-scenes delivery",
      "A flexible, trusted resource",
    ],
    packages: [
      {
        name: "Support Lite",
        price: "£400",
        period: "/month",
        subtitle: "Up to 6 hours per month.",
        features: [
          "Agreed task list",
          "Weekly email check-in",
          "Email or DM access",
          "Ideal for uploads, light scheduling and admin",
        ],
      },
      {
        name: "Support Core",
        price: "£625",
        period: "/month",
        subtitle: "Up to 10 hours per month.",
        highlighted: true,
        features: [
          "Flexible support across content, admin and marketing",
          "Weekly priorities",
          "Voice note and DM comms",
          "Light reporting or summaries on request",
        ],
      },
      {
        name: "Support Plus",
        price: "£850",
        period: "/month",
        subtitle: "Up to 15 hours per month.",
        features: [
          "Flexible scope across content, podcast, email, admin and more",
          "Priority comms via Slack, WhatsApp or voice notes",
          "Weekly summary check-ins",
          "Eligible for specialist add-ons",
        ],
      },
    ],
    addons: [
      "Additional hours - £40/hour",
      "Priority turnaround - £50/month",
      "Platform migration or content upload - from £75/project",
      "Notion, Trello or Google Drive setup - from £100",
    ],
    combinedWith: [
      { label: "Marketing Automation", slug: "marketing-automation" },
      { label: "Social Media Management", slug: "social-media-management" },
      { label: "Content Strategy", slug: "content-strategy" },
    ],
    faq: [
      { q: "How do we assign tasks?", a: "We agree a task list and weekly priorities, then communicate via email, DM, voice notes or Slack depending on your package." },
      { q: "What if I need more hours?", a: "You can add hours at £40/hour, or move up a package as your needs grow." },
    ],
  },
};

export const SERVICE_LIST = Object.values(SERVICES).map((s) => ({
  slug: s.slug,
  title: s.title,
}));

export const SERVICE_PILLARS = [
  {
    label: "Strategy",
    copy: "Direction before activity.",
    items: ["marketing-strategy", "content-strategy"],
    extra: ["Campaign Planning"],
  },
  {
    label: "Execution",
    copy: "Consistent delivery that keeps you visible.",
    items: ["social-media-management", "social-media-design", "website-design"],
    extra: ["Content Creation"],
  },
  {
    label: "Technology",
    copy: "Practical AI, automation and marketing support.",
    items: ["ai-consultancy", "marketing-automation", "virtual-marketing-support"],
    extra: [],
  },
];
